import { useEffect, useState } from 'react'
import { useReadContract, useReadContracts, useBlockNumber } from 'wagmi'
import { TENANT_CONFIG, TENANT_CONTRACTS } from '@config'
import { AVATAR_BOUND_ABI } from '@constants/Abi/avatarBoundAbi'
import { OPEN_MINT_ABI } from '@constants/Abi/openMintAbi'
import { CLOSED_CAPSULE } from '@constants/contracts'
import { useViewer } from '@hooks'
import { isStaging } from '@utils/isStaging'

interface ReturnType {
  hasMintedZeekNFT: boolean | null
  isLoadingHasMintedZeekNFT: boolean
  hasClosedCapsule: boolean | null
  closedCapsuleId: number | null
  isLoadingCheckCloseCapsule: boolean
}
const hasTokenGating = TENANT_CONFIG.features?.tokenGating

const useTokenGating = (): ReturnType => {
  const { viewer } = useViewer()
  const walletAddress = viewer?.walletAddress
  // Token used as gating mechanisnm
  const [hasMintedZeekNFT, setHasMintedZeekNFT] = useState<boolean | null>(null)
  // Token used for minting and onboarding logic
  const [hasClosedCapsule, setHasClosedCapsule] = useState<boolean | null>(null)
  const [closedCapsuleId, setClosedCapsuleId] = useState<number | null>(null)
  const [capsuleBalance, setCapsuleBalance] = useState<number | null>(null)

  // We check if the user have a NFT minted (Zeek)
  const {
    data: zeekBalanceData,
    isFetching: isFetchingHasMintedZeekNFT,
    status,
    refetch
  } = useReadContract({
    address: isStaging ? TENANT_CONTRACTS.testnet.avatar : TENANT_CONTRACTS.mainnet.avatar,
    abi: AVATAR_BOUND_ABI,
    functionName: 'balanceOf',
    chainId: TENANT_CONFIG.network.id,
    args: [walletAddress],
    query: { enabled: !!walletAddress && hasTokenGating }
  })

  const { data: blockNumber } = useBlockNumber({ watch: hasTokenGating && !hasMintedZeekNFT })
  useEffect(() => {
    if (hasTokenGating && !hasMintedZeekNFT) {
      refetch()
    }
  }, [blockNumber, hasMintedZeekNFT, hasTokenGating])

  useEffect(() => {
    if (status === 'success') {
      ;(zeekBalanceData as bigint)?.toString() === '0' ? setHasMintedZeekNFT(false) : setHasMintedZeekNFT(true)
    }
  }, [zeekBalanceData])

  // Check the capsule NFT balance
  const {
    data: capsuleBalanceData,
    isLoading: isLoadingCapsuleBalance,
    isFetching: isFetchingCapsuleBalance
  } = useReadContract({
    address: isStaging ? TENANT_CONTRACTS.testnet.nft_gate : TENANT_CONTRACTS.mainnet.nft_gate,
    abi: OPEN_MINT_ABI,
    functionName: 'balanceOf',
    chainId: TENANT_CONFIG.network.id,
    args: [walletAddress],
    query: { enabled: !!walletAddress && hasTokenGating }
  })

  useEffect(() => {
    if (capsuleBalanceData != null) {
      setCapsuleBalance(Number(capsuleBalanceData))
    }
  }, [capsuleBalanceData])

  const getArrayOfCapsulesNFTsForIds = () => {
    const result = []
    if (capsuleBalance != null) {
      for (let i = 0; i < capsuleBalance; i++) {
        result.push({
          address: isStaging ? TENANT_CONTRACTS.testnet.nft_gate : TENANT_CONTRACTS.mainnet.nft_gate,
          abi: OPEN_MINT_ABI,
          functionName: 'tokenOfOwnerByIndex',
          args: [walletAddress, i]
        })
      }
    }

    return result
  }

  const arrayOfNFTsToGetIds = getArrayOfCapsulesNFTsForIds()

  // Get Token IDs for all capsules
  const {
    data: capsuleIdsArray,
    isLoading: isLoadingGetTokenIds,
    isFetching: isFetchingGetTokensId
  } = useReadContracts({
    //@ts-ignore
    contracts: arrayOfNFTsToGetIds,
    chainId: TENANT_CONFIG.network.id,
    query: { enabled: !!walletAddress && arrayOfNFTsToGetIds?.length > 0 && hasTokenGating }
  })

  const getArrayOfTokenUris = () => {
    const result = []
    if (capsuleIdsArray) {
      for (let i = 0; i < capsuleIdsArray?.length; i++) {
        result.push({
          address: isStaging ? TENANT_CONTRACTS.testnet.nft_gate : TENANT_CONTRACTS.mainnet.nft_gate,
          abi: OPEN_MINT_ABI,
          functionName: 'tokenURI',
          args: [(capsuleIdsArray[i].result as bigint).toString()]
        })
      }
    }

    return result
  }

  const arrayOfTokenUris = getArrayOfTokenUris()

  // Get the token URIs array to know later if there is one is closed
  const { data: tokenUriArray, isLoading: isLoadingReadTokenUris } = useReadContracts({
    //@ts-ignore
    contracts: arrayOfTokenUris,
    query: { enabled: !!walletAddress && arrayOfTokenUris?.length > 0 && hasTokenGating }
  })

  useEffect(() => {
    if (tokenUriArray) {
      if (tokenUriArray?.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const walletClosedCapsule = tokenUriArray.some((item, index) => {
          if ((item?.result as string).includes(CLOSED_CAPSULE) && !!capsuleIdsArray) {
            setHasClosedCapsule(true)
            setClosedCapsuleId(Number(capsuleIdsArray[index].result))
          } else {
            setHasClosedCapsule(false)
          }
        })
      } else {
        setHasClosedCapsule(false)
      }
    }

    if (capsuleBalance === 0) {
      setHasClosedCapsule(false)
    }
  }, [tokenUriArray, capsuleBalance])

  // TODO: Check if we need this logic, it seems does not work as expected
  const isLoadingCheckCloseCapsule =
    isLoadingCapsuleBalance ||
    isFetchingCapsuleBalance ||
    isLoadingGetTokenIds ||
    isFetchingGetTokensId ||
    isLoadingReadTokenUris

  return {
    hasMintedZeekNFT,
    isLoadingHasMintedZeekNFT: isFetchingHasMintedZeekNFT,
    hasClosedCapsule,
    closedCapsuleId,
    isLoadingCheckCloseCapsule
  }
}

export default useTokenGating
